import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {withTranslation} from 'react-i18next';
import Screen from '../../../common/components/layout/Screen';
import Metadata from '../../../common/components/Metadata';
import {CancelButton, LoadingContainer, Span} from '../../../common/components/StyledComp';
import {faReceipt} from '@fortawesome/free-solid-svg-icons';
import {withRouter} from 'react-router';
import {Alert, Card, Container} from 'react-bootstrap';
import PaymentMethodManagement from './PaymentMethodManagement';
import styled from 'styled-components';
import {useQuery} from 'react-apollo';
import {PulseLoader} from 'react-spinners';
import gql from 'graphql-tag';
import AddSubscription from './AddSubscription';
import SubscriptionPlanInfo from './SubscriptionPlanInfo';
import {checkErrorType} from '../../../utils';
import {ERROR_TYPE_BAD_REQUEST} from '../../../common/constants';
import {withLoadedUser} from '../../../login/containers/AuthBase';

const SubscribeContent = styled(Container)`
  padding: 15px;
`;

const GRAPHQL_QUERY_BILLING_SUBSCRIPTION_PLAN = gql`
  query billingSubscriptionPlan($id: ID!) {
    billingSubscriptionPlan(id: $id) {
      id
      amount
      billingScheme
      currency
      interval
      intervalCount
      nickname
      position
      tiers {
        id
        flatAmount
        unitAmount
        upTo
      }
    }
  }
`;

const GRAPHQL_QUERY_USER_SUBSCRIPTION_INFO = gql`
  query userSubscriptionInfo($userId: ID!) {
    user(id: $userId) {
      id
      hasActiveSubscription
      hasValidPaymentMethod
      isActive
    }
  }
`;

const SubscribeFlow = ({t, history, match, currentUser, refetchCurrentUser, location}) => {
  const {userId, subscriptionPlanId} = match.params;
  const [confirmLoading, setConfirmLoading] = useState(false);

  const isOwner = currentUser.id === userId;

  const {loading: loadingUser, error: errorUser, data: dataUser} = useQuery(GRAPHQL_QUERY_USER_SUBSCRIPTION_INFO, {
    variables: {userId},
    fetchPolicy: 'network-only',
  });

  const {loading: loadingSubscriptionPlan, error: errorSubscriptionPlan, data: dataSubscriptionPlan} = useQuery(GRAPHQL_QUERY_BILLING_SUBSCRIPTION_PLAN, {
    variables: {id: subscriptionPlanId},
    fetchPolicy: 'network-only',
  });

  const onDone = () => {
    if (isOwner) {
      refetchCurrentUser();
    }
    setConfirmLoading(false);
    const {oldLocation} = location.state;
    let destUrl = '/admin/billing?newSubscription=true';
    if (oldLocation) {
      let search = new URLSearchParams(oldLocation.search ? oldLocation.search : '');
      search.set('newSubscription', 'true');
      destUrl = oldLocation.pathname + '?' + search.toString() + oldLocation.hash;
    }
    history.push(destUrl);
  };

  const user = dataUser && dataUser.user ? dataUser.user : null;
  const subscriptionPlan = dataSubscriptionPlan && dataSubscriptionPlan.billingSubscriptionPlan ? dataSubscriptionPlan.billingSubscriptionPlan : null;

  let content;
  if (loadingUser || loadingSubscriptionPlan) {
    content = (
      <LoadingContainer>
        <PulseLoader/>
      </LoadingContainer>
    );
  } else if (errorUser) {
    console.error(errorUser);
    content = (
      <>
        <Alert variant={'danger'}>{t('unknownError')}</Alert>
        <div className="popin-buttons">
          <CancelButton type="button" onClick={history.goBack} style={{marginTop: 0, width: 'auto'}}>{t('cancel')}</CancelButton>
        </div>
      </>
    );
  } else if (errorSubscriptionPlan) {
    const badRequestError = checkErrorType(errorSubscriptionPlan, ERROR_TYPE_BAD_REQUEST);
    let errorMessage;
    if (badRequestError) {
      console.warn(errorSubscriptionPlan);
      errorMessage = t('subscribeFlow.badRequestError');
    } else {
      console.error(errorSubscriptionPlan);
      errorMessage = t('unknownError');
    }
    content = (
      <>
        <Alert variant={'danger'}>{errorMessage}</Alert>
        <div className="popin-buttons">
          <CancelButton type="button" onClick={history.goBack} style={{marginTop: 0, width: 'auto'}}>{t('cancel')}</CancelButton>
        </div>
      </>
    );
  } else if (user === null) {
    content = (
      <>
        <Alert variant={'danger'}>{t('subscribeFlow.invalidUser')}</Alert>
        <div className="popin-buttons">
          <CancelButton type="button" onClick={history.goBack} style={{marginTop: 0, width: 'auto'}}>{t('cancel')}</CancelButton>
        </div>
      </>
    );
  } else if (subscriptionPlan === null) {
    content = (
      <>
        <Alert variant={'danger'}>{t('subscribeFlow.invalidSubscriptionPlan')}</Alert>
        <div className="popin-buttons">
          <CancelButton type="button" onClick={history.goBack} style={{marginTop: 0, width: 'auto'}}>{t('cancel')}</CancelButton>
        </div>
      </>
    );
  } else if (user.hasActiveSubscription) {
    content = (
      <>
        <p>{t('subscribeFlow.alreadyActiveSubscription')}</p>
        <div className="popin-buttons">
          <CancelButton type="button" onClick={history.goBack} style={{marginTop: 0, width: 'auto'}}>{t('cancel')}</CancelButton>
        </div>
      </>
    );
  } else if (isOwner && !user.isActive) {
    content = (
      <>
        <p>{t('subscribeFlow.inactiveAccount')}</p>
        <div className="popin-buttons">
          <CancelButton type="button" onClick={history.goBack} style={{marginTop: 0, width: 'auto'}}>{t('cancel')}</CancelButton>
        </div>
      </>
    );
  } else {
    content = (
      <>
        {!confirmLoading && (
          <>
            <Card>
              <Card.Body>
                <Card.Title>{t('subscribeFlow.paymentMethod')}</Card.Title>
                <PaymentMethodManagement userId={user.id}/>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <Card.Title>{t('subscribeFlow.subscription')}</Card.Title>
                <SubscriptionPlanInfo subscriptionPlan={subscriptionPlan}/>
              </Card.Body>
            </Card>

            {isOwner && !user.hasValidPaymentMethod && (
              <Alert variant={'info'}>{t('subscribeFlow.paymentMethodRequired')}</Alert>
            )}
            {!isOwner && !user.hasValidPaymentMethod && (
              <Alert variant={'info'}>{t('subscribeFlow.paymentMethodByMail')}</Alert>
            )}
          </>
        )}

        <AddSubscription
          userId={user.id}
          subscriptionId={subscriptionPlanId}
          onComplete={onDone}
          onCancel={history.goBack}
          onStart={() => setConfirmLoading(true)}
          onError={() => setConfirmLoading(false)}
          disabled={isOwner && !user.hasValidPaymentMethod}
        />
      </>
    );
  }

  return (
    <Screen
      header={
        <Metadata meta={t('subscribe')} title={t('subscribe')}/>
      }
      leftTitle={
        <>
          <FontAwesomeIcon icon={faReceipt} color={'rgb(201,0,95)'}/>
          <Span className="route-name">{t('subscribe')}</Span>
        </>
      }>
      <SubscribeContent>
        {content}
      </SubscribeContent>

    </Screen>
  );
};

SubscribeFlow.propTypes = {
  t: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      subscriptionPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      oldLocation: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
        search: PropTypes.string,
        hash: PropTypes.string,
      }),
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  refetchCurrentUser: PropTypes.func.isRequired,
};

export default compose(
  withTranslation('stripe'),
  withRouter,
  withLoadedUser,
)(SubscribeFlow);
